import { useAtomValue, useSetAtom } from 'jotai';
import {
  competitionAverageCompetitionIdsToFilterByAtom,
  competitionAverageSeasonIdsToFilterByAtom,
} from '@/atoms/filters/team/teamCompetitionAverageFilters';
import { teamLeagueAverageRadarFilterDebounceObject } from '@/atoms/queries/team/teamLeagueAverageRadar';
import { DEFAULT_DEBOUNCE_MS } from '@/consts/api';
import { useIsCompetitionAverageMode } from '@/hooks/useIsCompetitionAverageMode';
import { toObject } from '@/utils/object';
import { useDeepEffect } from '../useDeepEffect';

export const useSetTeamLeagueAverageDebouncedFilterParams = (debounceTimeout: number = DEFAULT_DEBOUNCE_MS) => {
  const isCompetitionAverageMode = useIsCompetitionAverageMode();
  const competitionIds = useAtomValue(competitionAverageCompetitionIdsToFilterByAtom);
  const seasonIds = useAtomValue(competitionAverageSeasonIdsToFilterByAtom);

  const { debouncedValueAtom: leagueAverageRadarFilterAtom, timeoutDelayAtom } =
    teamLeagueAverageRadarFilterDebounceObject;
  const setLeagueAverageRadarFilter = useSetAtom(leagueAverageRadarFilterAtom);
  const setTimeoutDelay = useSetAtom(timeoutDelayAtom);

  useDeepEffect(() => {
    // null causes hasEmptyInFilters to short-circuit, so no request fires
    if (!isCompetitionAverageMode) {
      setLeagueAverageRadarFilter(null);
      return;
    }

    // No competition resolved yet — fire immediately (the query short-circuits) rather than debounce.
    if (!competitionIds.length) setTimeoutDelay(0);
    // Teams reject an `in[position]` filter (the endpoint schema is strict), so it is omitted here.
    setLeagueAverageRadarFilter({
      in: {
        competition_id: competitionIds,
        ...toObject('season_id', seasonIds),
      },
    });
    // Once a competition resolves, debounce subsequent selection changes.
    if (competitionIds.length) setTimeoutDelay(debounceTimeout);
  }, [isCompetitionAverageMode, competitionIds, seasonIds]);
};
